import { MediaRequirements } from '@/app/(main)/build/dynamicSections';
import { ShopifyProductVariant } from '@/app/api/shopify/shopify';
import { StoreCode } from '@/types';
import { OfferLocaleType, OfferMediaDeviceType } from '@/types/offer';
import { OfferLocaleRestrictionsType } from '@/types/offer/builder';
import { validateFutureDate } from '@/utils/form/frontendValidators';
import { validateRequired } from '@/utils/form/validators';

/* --------- configs --------- */

export const STORE_CODE_TO_POSSIBLE_LOCALES_MAP: Record<StoreCode, OfferLocaleType[]> = {
  eu: ['de', 'eu', 'nl', 'se', 'es', 'fr'],
  uk: ['gb'],
  us: ['us', 'apac-us', 'au', 'nz'],
  jp: [],
  dev: [],
  qa: [],
  eud: [],
  ukd: [],
  aud: [],
  nzd: []
};

export const FREE_SHOPIFY_PRODUCT_IDENTIFIER_TAGS = ['Welcome Gift', 'Welcome Kit'];

/* --------- form values --------- */

export const OFFER_DEFAULT_LOCALE = 'us' as OfferLocaleType;

export const OFFER_FORM_ERROR_HEX_CODE = '#d32f2f';

export const OFFER_DEFAULT_HEADLINE_VALUE = 'Get started with your AG1 Welcome Kit**';

export const MAX_DISCOUNTS_PER_OFFER = 5;

export const MAX_DISCOUNTS_PER_OFFER_REACHED_WARNING =
  'A maximum limit of 5 discounted items is allowed per offer. Any additional products added will be marked at original price.';

// TODO: put these into DB and change them to the right values
export const DEFAULT_SINGLE_OPTION_DESKTOP_HERO_IMAGE =
  'image-8dc3a6f0d495b9f3942deb69d18a180d55719b8e-906x1200-jpg';
export const DEFAULT_SINGLE_OPTION_MOBILE_HERO_IMAGE =
  'image-bf395e3309554cc61332dad7812488af43e65465-1125x751-png';

// TODO: put this into DB and change it to the right value
export const DEFAULT_MULTIPLE_OPTIONS_ADDON_PLACEHOLDER_IMAGE =
  'image-df58005576d312f8aaba38b26915ff8496da57d9-960x960-png';

// TODO: put these into DB and change them to the right values
export const DEFAULT_MULTIPLE_OPTIONS_ADDON_IMAGE: Record<string, string> = {
  'AG1 Shaker': 'image-1addbfbb678c935ad1c43c28c4e9bb969d7ca18f-1413x1356-png',
  'Exclusive New Omega': 'image-0065d479dcdf3c9a3b85c95ef506578bb08c67eb-4096x4096-png',
  'Vitamin D3+K2': 'image-4133bfc92c6c10d3e72454fe455296ad851d5e1c-750x750-png',
  'AG1 Travel Packs (5 count)': 'image-673cd9415b02927c114e3abde0f14bf785b15d9b-1320x1320-png',
  'AG Omega3': 'image-d337c674140c77bcbfe1b41fe62601976d45bb83-331x330-png',
  'AG1 Welcome Kit': 'image-ba31fabf270497573c1772431046030a826ce257-2500x1875-jpg',
  'AG1 Travel Packs (30 count)': 'image-8aaf74aba274d6522b96072fb041f24111351456-1320x1320-png',
  'Refer a Friend Flyer': 'image-025be9c4acc6437f80ca5d35f4ab865c709350dd-666x666-png',
  'AG1 Hat': 'image-7f56f962a27d8b1c3d911c0dd33a4bbcf8a7b222-800x600-jpg',
  'AG1 Travel Packs (10 count)': 'image-46ac79fb04226160819800993cdb6de8dab453e1-2109x2109-png',
  'Morning Hat': 'image-7f56f962a27d8b1c3d911c0dd33a4bbcf8a7b222-800x600-jpg',
  'AG1 Shaker (x2)': 'image-1addbfbb678c935ad1c43c28c4e9bb969d7ca18f-1413x1356-png',
  'AG1 Metal Scoop': 'image-baec1cf5c43b52558b8207b579e89c78b218995e-2109x2110-png',
  'AG1 Shaker (single)': 'image-1addbfbb678c935ad1c43c28c4e9bb969d7ca18f-1413x1356-png'
};

export const DEFAULT_EMPTY_MULTIPLE_OPTIONS_CUSTOMIZATION_VALUE = {
  subBullets: [],
  subDescription: '',
  subTitle: ''
};

export const MISSING_PRODUCT_SELECTION_ERROR = 'At least one product selection is required';

export const MULTIPLE_OPTIONS_CORE_PRODUCT_SELECTION_CONFIG_ERRORS = {
  MISSING_CUSTOMIZATION_TITLE: 'Subscription title is required',
  MISSING_CUSTOMIZATION_DESCRIPTION: 'Subscription description is required',
  MISSING_CUSTOMIZATION_BULLETS: 'At least one bullet is required',
  MISSING_CUSTOMIZATION_BULLET_ENTRY: 'Subscription bullets cannot be empty',
  MISSING_CUSTOMIZATION_BADGE: 'Badge is required',
  MISSING_MEDIA_DESKTOP: 'Desktop image is required',
  MISSING_MEDIA_MOBILE: 'Mobile image is required'
};

export const AG1_FAMILY_PLAN_SHAKER_PRODUCT_VARIANT_ID = '41518710325346';

export const DEFAULT_EMPTY_SHOPIFY_VARIANT_DATA = {
  id: 'placeholder-shopify-variant',
  title: 'Subscription Title',
  price: '9.99',
  availableForSale: true,
  product: {
    title: 'Product Title',
    featuredMedia: undefined,
    description: 'Product Description',
    descriptionHtml: '<p>Product Description</p>',
    handle: 'product-handle',
    id: '',
    tags: []
  }
} as ShopifyProductVariant;

/* --------- form types --------- */

// IMPORTANT: do not change the ordering of this object
// the index here serves as the ranking for rendering
// the locale tabs in offer builder header
export const OFFER_LOCALE = {
  US: 'us',
  GB: 'gb',
  DE: 'de',
  NL: 'nl',
  SE: 'se',
  EU: 'eu',
  ES: 'es',
  FR: 'fr',
  APAC_US: 'apac-us',
  AU: 'au',
  NZ: 'nz'
  // increase this list as more regions become available in offer builder
} as const;

export const OFFER_STATUS = {
  Draft: 'draft',
  Published: 'published',
  Archived: 'archived'
} as const;

export const OFFER_TRANSACTION = {
  MultipleOptionsOffer: 'customer_choice',
  SingleOptionOffer: 'direct_checkout'
} as const;

export const OFFER_PRODUCT_USAGE = {
  Addon: 'addon',
  DirectCheckout: 'direct_checkout',
  FreeTrial: 'free_trial',
  Subscription: 'subscription'
} as const;

export const OFFER_TRANSACTION_TAGLINE = {
  MultipleOptionsOffer: 'Customer selects their preferred bundle.',
  SingleOptionOffer: 'One predefined bundle of products. Direct to Checkout.'
} as const;

export const OFFER_PLAN = {
  Standard: 'standard',
  FirstMonthFree: 'free_period',
  FreeTrial: 'free_trial',
  Discount: 'discount'
} as const;

export const OFFER_MULTIPLE_OPTIONS_PLAN = {
  Standard: 'standard',
  FirstMonthFree: 'free_period',
  FreeTrial: 'free_trial'
} as const;

export const OFFER_SINGLE_OPTION_PLAN = {
  Standard: 'standard',
  Discount: 'discount'
} as const;

export const OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY = {
  Pouch: 'pouch',
  TravelPack: 'travel-pack',
  Other: 'other'
} as const;

export const OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY = {
  FirstOrderOnly: 'first_order_only',
  Recurring: 'recurring' // subscription
} as const;

export const OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE = {
  None: 'none',
  Percentage: 'percentage',
  Value: 'value',
  Free: 'free'
} as const;

export const OFFER_SINGLE_OPTION_PRODUCT_CATEGORY = {
  Shaker: 'shaker',
  TravelPack5Count: 'travel-pack-5-count',
  Scoop: 'scoop'
  // TODO: expand this list
} as const;

export const OFFER_SINGLE_OPTION_DISCOUNT_TYPE = {
  WholeOrder: 'whole_order',
  PerProduct: 'per_product'
} as const;

export const OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE = {
  Percentage: 'percentage',
  Value: 'absolute'
} as const;

export const OFFER_MULTIPLE_OPTIONS_DEFAULT_BADGE_VALUE = {
  MostPopular: 'Most Popular',
  BestValue: 'Best Value'
} as const;

export const OFFER_MEDIA_DEVICE_TYPE = {
  Desktop: 'desktop',
  Mobile: 'mobile'
} as const;

export const OFFER_INVALID_REASON = {
  SoldOut: 'sold-out',
  Paused: 'paused',
  UnsupportedLocale: 'unsupported-locale',
  Expired: 'expired',
  TooEarly: 'too-early'
} as const;

export const OFFER_EXPIRY_TYPES = {
  None: null,
  Indefinite: 'indefinite',
  Date: 'date',
  Count: 'count',
  DateAndCount: 'dateAndCount'
} as const;

/* --------- lists --------- */

export const OFFER_LOCALE_LIST = Object.values(OFFER_LOCALE);

export const OFFER_EXPIRY_TYPES_LIST = Object.values(OFFER_EXPIRY_TYPES);

export const OFFER_STATUS_LIST = Object.values(OFFER_STATUS);

export const OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY_LIST = Object.values(
  OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY
);

export const OFFER_SINGLE_OPTION_PRODUCT_CATEGORY_LIST = Object.values(
  OFFER_SINGLE_OPTION_PRODUCT_CATEGORY
);

export const OFFER_SINGLE_OPTION_DISCOUNT_TYPE_OPTIONS = Object.values(
  OFFER_SINGLE_OPTION_DISCOUNT_TYPE
);

export const OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE_LIST = Object.values(
  OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE
);

export const OFFER_TRANSACTION_OPTIONS = Object.values(OFFER_TRANSACTION);

export const OFFER_TRANSACTION_TAGLINE_LIST = Object.values(OFFER_TRANSACTION_TAGLINE) as string[];

export const AVAILABLE_MULTIPLE_OPTIONS_OFFER_PLANS = Object.values(OFFER_MULTIPLE_OPTIONS_PLAN);

export const OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY_LIST = Object.values(
  OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY
);

export const OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE_LIST = Object.values(
  OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE
);

export const OFFER_REBILL_DAYS_OPTIONS = [5, 10, 20, 30]; // TODO: update this list

export const AVAILABLE_SINGLE_OPTION_OFFER_PLANS = Object.values(OFFER_SINGLE_OPTION_PLAN);

export const OFFER_MEDIA_DEVICE_TYPE_LIST = Object.values(OFFER_MEDIA_DEVICE_TYPE);

export const INVALID_OFFER_REASON_LIST = Object.values(OFFER_INVALID_REASON);

/* --------- media --------- */

export const MULTIPLE_OPTIONS_OFFER_MEDIA_RESTRICTIONS = {
  mobile: {
    aspectWidth: 16,
    aspectHeight: 9,
    minWidth: 675,
    minHeight: 1_200,
    formats: ['.png']
  },
  desktop: {
    aspectWidth: 1,
    aspectHeight: 1,
    minWidth: 1_200,
    minHeight: 1_200,
    formats: ['.png']
  }
} satisfies Record<OfferMediaDeviceType, MediaRequirements>;

// TODO: update this config
export const SINGLE_OPTION_OFFER_MEDIA_RESTRICTIONS = MULTIPLE_OPTIONS_OFFER_MEDIA_RESTRICTIONS;

export const OFFER_LOCALE_RESTRICTIONS = {
  dateFrom: [(data, initialData) => validateRequired(data.dateFrom, initialData.dateFrom)],
  dateTo: [
    (data, initialData) => validateRequired(data.dateTo, initialData.dateTo),
    (data, initialData) =>
      validateFutureDate(
        data.dateTo ? new Date(data.dateTo) : null,
        initialData.dateTo ? new Date(initialData.dateTo) : null
      )
  ],
  quantity: [(data, initialData) => validateRequired(data.quantity, initialData.quantity)]
} as const satisfies Readonly<OfferLocaleRestrictionsType>;
